import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, MagnifyingGlass, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money, shortDate, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

const COMPLIANCE = {
  compliant: "bg-emerald-50 text-emerald-700 border-emerald-300",
  review_needed: "bg-amber-50 text-amber-800 border-amber-300",
  non_compliant: "bg-alert/10 text-alert border-alert/40",
};

export default function Grants() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const canCreate = ["grant_manager", "admin"].includes(user?.role);

  const load = async () => {
    const { data } = await api.get("/grants", { params: search ? { search } : {} });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div>
      <PageHeader
        overline="Grants Module"
        title="Grants"
        actions={
          canCreate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="new-grant-btn"><Plus size={16} className="mr-1.5" /> New Grant</Button></DialogTrigger>
              <NewGrantDialog onDone={() => { setOpen(false); load(); }} />
            </Dialog>
          )
        }
      >
        Funding source records with grant-level requirements, obligations, and roll-ups across linked contracts.
      </PageHeader>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="grant-search" className="pl-9" placeholder="Search grants…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        <Button variant="outline" onClick={load}>Search</Button>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="No grants found" subtitle="Create a grant to begin linking contracts and tracking funding." />
      ) : (
        <div className="bg-card border border-border rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Grant</TableHead>
                <TableHead>Total Funding</TableHead>
                <TableHead>Remaining</TableHead>
                <TableHead>Contracts</TableHead>
                <TableHead>Compliance</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((g) => (
                <TableRow key={g.id} data-testid={`grant-row-${g.id}`} className="cursor-pointer" onClick={() => navigate(`/grants/${g.id}`)}>
                  <TableCell>
                    <div className="font-medium">{g.title}</div>
                    <div className="text-xs text-muted-foreground">{g.funding_source} · {shortDate(g.grant_period_start)}–{shortDate(g.grant_period_end)}</div>
                  </TableCell>
                  <TableCell className="text-sm font-medium">{money(g.grant_amount)}</TableCell>
                  <TableCell className="text-sm">{money(g.remaining_funds)}</TableCell>
                  <TableCell className="text-sm">{g.linked_contract_count}</TableCell>
                  <TableCell>
                    <span className={`inline-flex px-2.5 py-0.5 rounded-sm border text-xs font-medium ${COMPLIANCE[g.compliance_status] || COMPLIANCE.review_needed}`}>
                      {(g.compliance_status || "").replace(/_/g, " ")}
                    </span>
                  </TableCell>
                  <TableCell><CaretRight size={16} className="text-muted-foreground" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function NewGrantDialog({ onDone }) {
  const [form, setForm] = useState({
    title: "", funding_source: "", grant_amount: "", grant_period_start: "", grant_period_end: "",
    reporting_obligations: "", compliance_status: "compliant",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    if (!form.title) return toast.error("Title is required");
    setBusy(true);
    try {
      await api.post("/grants", { ...form, grant_amount: parseFloat(form.grant_amount) || 0 });
      toast.success("Grant created");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="max-w-xl">
      <DialogHeader><DialogTitle className="font-head">New Grant</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 space-y-1.5"><Label>Grant title</Label><Input data-testid="ng-title" value={form.title} onChange={set("title")} /></div>
        <div className="col-span-2 space-y-1.5"><Label>Funding source</Label><Input data-testid="ng-source" value={form.funding_source} onChange={set("funding_source")} placeholder="e.g. HHS Federal Block Grant" /></div>
        <div className="space-y-1.5"><Label>Grant amount ($)</Label><Input data-testid="ng-amount" type="number" value={form.grant_amount} onChange={set("grant_amount")} /></div>
        <div className="space-y-1.5"><Label>Period start</Label><Input data-testid="ng-start" type="date" value={form.grant_period_start} onChange={set("grant_period_start")} /></div>
        <div className="space-y-1.5"><Label>Period end</Label><Input data-testid="ng-end" type="date" value={form.grant_period_end} onChange={set("grant_period_end")} /></div>
        <div className="col-span-2 space-y-1.5"><Label>Reporting obligations</Label><Input data-testid="ng-reporting" value={form.reporting_obligations} onChange={set("reporting_obligations")} placeholder="e.g. Quarterly FSR + Annual report" /></div>
      </div>
      <DialogFooter><Button data-testid="ng-submit" onClick={submit} disabled={busy}>{busy ? "Creating…" : "Create Grant"}</Button></DialogFooter>
    </DialogContent>
  );
}
